import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import Box             from '@mui/material/Box';
import Grid            from '@mui/material/Grid';
import Typography      from '@mui/material/Typography';
import Tooltip         from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert           from '@mui/material/Alert';
import Chip            from '@mui/material/Chip';
import Dialog          from '@mui/material/Dialog';
import DialogTitle     from '@mui/material/DialogTitle';
import DialogContent   from '@mui/material/DialogContent';
import DialogActions   from '@mui/material/DialogActions';
import Button          from '@mui/material/Button';
import IconButton      from '@mui/material/IconButton';
import Table           from '@mui/material/Table';
import TableBody       from '@mui/material/TableBody';
import TableCell       from '@mui/material/TableCell';
import TableContainer  from '@mui/material/TableContainer';
import TableHead       from '@mui/material/TableHead';
import TableRow        from '@mui/material/TableRow';
import Paper           from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import Divider         from '@mui/material/Divider';
import ToggleButton    from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CloseIcon           from '@mui/icons-material/Close';
import InfoOutlinedIcon    from '@mui/icons-material/InfoOutlined';
import PeopleAltIcon       from '@mui/icons-material/PeopleAlt';
import StoreIcon           from '@mui/icons-material/Store';
import FactoryIcon         from '@mui/icons-material/Factory';
import AccountBalanceIcon  from '@mui/icons-material/AccountBalance';
import FlagIcon            from '@mui/icons-material/Flag';
import CalendarMonthIcon   from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon  from '@mui/icons-material/EventAvailable';
import TextField        from '@mui/material/TextField';
import MenuItem         from '@mui/material/MenuItem';
import Select           from '@mui/material/Select';
import FormControl      from '@mui/material/FormControl';
import InputLabel       from '@mui/material/InputLabel';
import SearchIcon       from '@mui/icons-material/Search';
import DownloadIcon     from '@mui/icons-material/Download';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InputAdornment   from '@mui/material/InputAdornment';
import Card      from 'components/Card/Card.js';
import CardBody  from 'components/Card/CardBody.js';
import moneyFormat from 'helpers/moneyFormat';
import { useExport } from 'context/ExportContext';

const CALF_GREEN      = '#8ebf63';
const CALF_GREEN_DARK = '#39a82d';
const CALF_BLACK      = '#161616';
const CALF_LIGHT      = '#bee59a';
const GREY_TEXT       = '#757575';
const DONUT_COLORS    = ['#5c9e3a','#8ebf63','#bee59a','#3a7abf','#bf7a3a'];

const TIPO_META = {
  'Residencial':       { icon: <PeopleAltIcon />,     color: '#5c9e3a' },
  'Tarifa General':    { icon: <StoreIcon />,          color: '#8ebf63' },
  'Grandes Usuarios':  { icon: <FactoryIcon />,        color: '#bee59a' },
  'Estado Provincial': { icon: <AccountBalanceIcon />, color: '#3a7abf' },
  'Estado Nacional':   { icon: <FlagIcon />,           color: '#bf7a3a' },
};

const fmt = (val) => {
  if (val == null || isNaN(val)) return '—';
  if (Math.abs(val) >= 1e6)
    return `$${(val / 1e6).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}M`;
  return moneyFormat(val);
};
const fmtFull = (val) => (val == null || isNaN(val) ? '—' : moneyFormat(val));

// ─── Toggle filtro de fecha ───────────────────────────────────────────────────
function FiltroFechaToggle({ value, onChange }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      px: 2, py: 1,
      bgcolor: '#f5f5f5',
      borderBottom: `2px solid ${CALF_LIGHT}`,
    }}>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: GREY_TEXT }} />
      <Typography variant="caption" sx={{ color: GREY_TEXT, fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5 }}>
        Criterio de exclusión del mes actual:
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_, v) => { if (v) onChange(v); }}
        size="small"
        sx={{ bgcolor: '#fff' }}
      >
        <ToggleButton value="periodo" sx={{
          px: 2, fontSize: '0.75rem', fontWeight: 600,
          '&.Mui-selected': { bgcolor: CALF_GREEN_DARK, color: '#fff', '&:hover': { bgcolor: CALF_GREEN } },
        }}>
          <CalendarMonthIcon sx={{ fontSize: 15, mr: 0.5 }} />
          Por Período
        </ToggleButton>
        <ToggleButton value="compvto" sx={{
          px: 2, fontSize: '0.75rem', fontWeight: 600,
          '&.Mui-selected': { bgcolor: CALF_GREEN_DARK, color: '#fff', '&:hover': { bgcolor: CALF_GREEN } },
        }}>
          <EventAvailableIcon sx={{ fontSize: 15, mr: 0.5 }} />
          Por Vencimiento
        </ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" sx={{ color: GREY_TEXT, fontStyle: 'italic' }}>
        {value === 'periodo'
          ? 'Excluye registros con PeriodoDeVto = mes actual'
          : 'Excluye registros con fecha de vencimiento en el mes actual'}
      </Typography>
    </Box>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ data, onClick, loading }) {
  if (loading) {
    return (
      <Box sx={{ minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${CALF_LIGHT}`, borderRadius: 2, bgcolor: '#fff' }}>
        <CircularProgress size={28} sx={{ color: CALF_GREEN }} />
      </Box>
    );
  }
  if (!data) return null;
  const meta = TIPO_META[data.tipo] || {};
  return (
    <Tooltip title={`Click para ver los ${data.gerencial_registros?.toLocaleString('es-AR')} comprobantes`} placement="top">
      <Box onClick={() => onClick(data)} sx={{
        cursor: 'pointer', borderRadius: 2,
        border: `1px solid ${CALF_LIGHT}`, borderLeft: `5px solid ${meta.color || CALF_GREEN}`,
        p: 2, bgcolor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.07)',
        transition: 'box-shadow .15s, transform .15s',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,.13)', transform: 'translateY(-2px)' },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{ color: meta.color || CALF_GREEN, display: 'flex' }}>{meta.icon}</Box>
          <Typography variant="caption" sx={{ fontWeight: 700, color: CALF_BLACK, textTransform: 'uppercase', letterSpacing: .5 }}>
            {data.tipo}
          </Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: CALF_GREEN_DARK, lineHeight: 1.1 }}>
          {fmt(data.gerencial)}
        </Typography>
        <Typography variant="caption" sx={{ color: GREY_TEXT, display: 'block', mt: 0.5 }}>
          {data.gerencial_registros?.toLocaleString('es-AR')} comprobantes
        </Typography>
      </Box>
    </Tooltip>
  );
}

// ─── Donut Chart ──────────────────────────────────────────────────────────────
function DonutDeuda({ totales }) {
  const labels = totales.map(t => t.tipo);
  const series = totales.map(t => Math.max(0, t.gerencial));
  const total  = series.reduce((a, b) => a + b, 0);
  const options = {
    chart: { type: 'donut', fontFamily: 'inherit' },
    labels, colors: DONUT_COLORS,
    legend: { position: 'bottom', fontSize: '12px' },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%`,
      style: { fontSize: '11px', fontWeight: 600 }, dropShadow: { enabled: false } },
    plotOptions: { pie: { donut: { size: '68%', labels: { show: true,
      value: {
        show: true,
        fontSize: '14px',
        fontWeight: 700,
        color: CALF_BLACK,
        formatter: (val) => fmt(Number(val)),
      },
      total: {
        show: true,
        label: 'Total',
        fontSize: '12px',
        fontWeight: 700,
        color: CALF_BLACK,
        formatter: () => fmt(total),
      },
    } } } },
    tooltip: { y: { formatter: (val) => fmtFull(val) } },
    stroke: { width: 2 },
  };
  return <ReactApexChart options={options} series={series} type="donut" height={320} />;
}

// ─── Modal Drill-down ─────────────────────────────────────────────────────────
function DrillDownModal({ open, onClose, tipo, filtroFecha }) {
  const [rows,          setRows]          = useState([]);
  const [total,         setTotal]         = useState(0);
  const [page,          setPage]          = useState(0);
  const [rowsPP,        setRowsPP]        = useState(50);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [filtroNombre,  setFiltroNombre]  = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState('');
  const [filtroEstado,  setFiltroEstado]  = useState('');
  const [sinResultados, setSinResultados] = useState(false);

  // Apertura progresiva (Mantis #303/#480): 'resumen' (por período de vto) → 'detalle'.
  const [vista,          setVista]          = useState('resumen');
  const [resumen,        setResumen]        = useState([]);
  const [loadingResumen, setLoadingResumen] = useState(false);

  const { startExport } = useExport();

  const fetchResumen = async () => {
    if (!tipo) return;
    setLoadingResumen(true); setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/deudapor-tipo/resumen-vto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
        body: JSON.stringify({ tipo, filtroFecha }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
      setResumen(json.data ?? []);
    } catch (e) { setError(e.message); }
    finally { setLoadingResumen(false); }
  };

  const fetchDetalle = async (pg, sz, nombre, periodo, estado) => {
    if (!tipo) return;
    setLoading(true); setError(null); setSinResultados(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/deudapor-tipo/detalle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
        body: JSON.stringify({
          tipo, page: pg, size: sz, filtroFecha,
          filtroNombre:  nombre,
          filtroPeriodo: periodo,
          filtroEstado:  estado,
        }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
      setRows(json.data ?? []);
      setTotal(json.total ?? 0);
      if ((json.total ?? 0) === 0) setSinResultados(true);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && tipo) {
      // Al abrir mostramos el resumen por período de vencimiento (nivel intermedio).
      setVista('resumen');
      setFiltroNombre(''); setFiltroPeriodo(''); setFiltroEstado('');
      setSinResultados(false); setPage(0);
      setRows([]); setTotal(0);
      fetchResumen();
    }
    if (!open) {
      setVista('resumen'); setResumen([]);
      setRows([]); setTotal(0);
      setFiltroNombre(''); setFiltroPeriodo(''); setFiltroEstado('');
      setSinResultados(false); setPage(0);
    }
  }, [open, tipo, filtroFecha]);

  // Nivel 3: entrar al detalle de comprobantes de un período elegido en el resumen.
  const abrirPeriodo = (periodo) => {
    setFiltroPeriodo(periodo);
    setFiltroNombre(''); setFiltroEstado('');
    setSinResultados(false); setPage(0);
    setVista('detalle');
    fetchDetalle(0, rowsPP, '', periodo, '');
  };

  const volverResumen = () => {
    setVista('resumen');
    setSinResultados(false);
    setFiltroNombre(''); setFiltroEstado('');
  };

  // Ejecutar búsqueda con los filtros actuales (llamado manualmente)
  const ejecutarBusqueda = () => {
    setPage(0);
    fetchDetalle(0, rowsPP, filtroNombre, filtroPeriodo, filtroEstado);
  };

  const handleNombreKeyDown = (e) => {
    if (e.key === 'Enter') ejecutarBusqueda();
  };

  const handleEstadoChange = (e) => {
    const val = e.target.value;
    setFiltroEstado(val);
    setPage(0);
    fetchDetalle(0, rowsPP, filtroNombre, filtroPeriodo, val);
  };

  const handleLimpiar = () => {
    setFiltroNombre(''); setFiltroEstado('');
    setSinResultados(false);
    setPage(0);
    fetchDetalle(0, rowsPP, '', filtroPeriodo, '');
  };

  const handlePageChange = (_, p) => {
    setPage(p);
    fetchDetalle(p, rowsPP, filtroNombre, filtroPeriodo, filtroEstado);
  };

  const handleRowsPerPageChange = (e) => {
    const sz = +e.target.value;
    setRowsPP(sz);
    setPage(0);
    fetchDetalle(0, sz, filtroNombre, filtroPeriodo, filtroEstado);
  };

  const handleExport = (format) => {
    const params = new URLSearchParams({ tipo, format, filtroFecha });
    if (filtroNombre.trim()) params.set('filtroNombre', filtroNombre.trim());
    if (filtroPeriodo.trim()) params.set('filtroPeriodo', filtroPeriodo.trim());
    if (filtroEstado.trim()) params.set('filtroEstado', filtroEstado.trim());
    const url = `${process.env.REACT_APP_API_URL}/deudapor-tipo/export?${params}`;
    startExport({ format, label: `Deuda ${tipo}`, customUrl: url });
  };

  const meta = TIPO_META[tipo] || {};
  const hayFiltros = filtroNombre || filtroEstado;
  const periodoLabel = (p) => (p === 'Anteriores' ? 'Anteriores' : String(p || '').replace(/^P/, ''));
  const totalResumen = resumen.reduce((s, r) => s + (r.importe || 0), 0);

  const COLS = [
    { id: 'persona',          label: 'Persona',      width: 70  },
    { id: 'nombre',           label: 'Nombre',       width: 180 },
    { id: 'domicilio',        label: 'Domicilio',    width: 180 },
    { id: 'comprobante',      label: 'Comprobante',  width: 140 },
    { id: 'fecha_comprobante',label: 'F. Cpte.',     width: 90  },
    { id: 'importe',          label: 'Importe',      width: 120, align: 'right' },
    { id: 'fecha_vto',        label: 'Vto.',         width: 90  },
    { id: 'periodo_vto',      label: 'Período',      width: 90  },
    { id: 'categoria',        label: 'Tarifa',       width: 130 },
    { id: 'estado_deuda',     label: 'Estado Deuda', width: 110 },
    { id: 'estado_servicio',  label: 'Servicio',     width: 90  },
    { id: 'dias_interes',     label: 'Días',         width: 60, align: 'right' },
    { id: 'marca',            label: 'Marca',        width: 60  },
    { id: 'submarca',         label: 'SubMarca',     width: 160 },
    { id: 'localidad',        label: 'Localidad',    width: 120 },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `3px solid ${meta.color || CALF_GREEN}`, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ color: meta.color || CALF_GREEN }}>{meta.icon}</Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: CALF_BLACK, lineHeight: 1.1 }}>
              {tipo}
            </Typography>
            <Typography variant="caption" sx={{ color: GREY_TEXT }}>
              {vista === 'resumen'
                ? `Deuda por período de vencimiento · ${filtroFecha === 'periodo' ? 'filtro por Período' : 'filtro por Vencimiento'}`
                : `Período ${periodoLabel(filtroPeriodo)} · ${total.toLocaleString('es-AR')} comprobantes`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {/* ── Nivel 2: resumen por período de vencimiento ── */}
        {vista === 'resumen' && (
          loadingResumen ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: CALF_GREEN }} />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 'calc(90vh - 200px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {['Período de Vencimiento', 'Importe', 'Comprobantes', ''].map((h, i) => (
                      <TableCell key={i} align={i === 1 || i === 2 ? 'right' : 'left'} sx={{
                        fontWeight: 700, fontSize: '0.78rem', bgcolor: '#f5f5f5', color: CALF_BLACK,
                        borderBottom: `3px solid ${meta.color || CALF_GREEN}`, py: 1 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resumen.map((r, idx) => (
                    <TableRow key={r.periodo_vto} hover onClick={() => abrirPeriodo(r.periodo_vto)}
                      sx={{ cursor: 'pointer', bgcolor: idx % 2 === 0 ? '#fff' : '#f7faf3',
                        '&:hover': { bgcolor: '#e8f4d9' } }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarMonthIcon sx={{ fontSize: 16, color: meta.color || CALF_GREEN }} />
                          {periodoLabel(r.periodo_vto)}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: CALF_GREEN_DARK }}>{fmtFull(r.importe)}</TableCell>
                      <TableCell align="right">{r.registros.toLocaleString('es-AR')}</TableCell>
                      <TableCell align="right"><ChevronRightIcon sx={{ fontSize: 18, color: GREY_TEXT }} /></TableCell>
                    </TableRow>
                  ))}
                  {resumen.length > 0 && (
                    <TableRow sx={{ bgcolor: '#f0f7e8' }}>
                      <TableCell sx={{ fontWeight: 800 }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: CALF_GREEN_DARK }}>{fmtFull(totalResumen)}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  )}
                  {resumen.length === 0 && (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: GREY_TEXT }}>
                      Sin datos para este grupo
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )
        )}

        {/* ── Nivel 3: detalle de comprobantes del período ── */}
        {vista === 'detalle' && (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, bgcolor: '#f9f9f9',
              borderBottom: '1px solid #eee', flexWrap: 'wrap', alignItems: 'center' }}>
              <Button size="small" startIcon={<ArrowBackIcon />} onClick={volverResumen}
                sx={{ textTransform: 'none', color: CALF_GREEN_DARK, fontWeight: 600 }}>
                Volver a períodos
              </Button>
              <Chip icon={<CalendarMonthIcon />} label={`Vencimiento: ${periodoLabel(filtroPeriodo)}`} size="small"
                sx={{ bgcolor: '#eef6e3', color: CALF_BLACK, fontWeight: 600 }} />
              <TextField
                size="small"
                placeholder="Buscar por nombre... (Enter para buscar)"
                value={filtroNombre}
                onChange={(e) => setFiltroNombre(e.target.value)}
                onKeyDown={handleNombreKeyDown}
                sx={{ minWidth: 240, bgcolor: '#fff' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: GREY_TEXT }} />
                    </InputAdornment>
                  ),
                  endAdornment: filtroNombre ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={ejecutarBusqueda} sx={{ color: CALF_GREEN_DARK }}>
                        <SearchIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <FormControl size="small" sx={{ minWidth: 180, bgcolor: '#fff' }}>
                <InputLabel>Estado de Deuda</InputLabel>
                <Select value={filtroEstado} label="Estado de Deuda" onChange={handleEstadoChange}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Deuda Vencida">Deuda Vencida</MenuItem>
                  <MenuItem value="Deuda no Vencida">Deuda no Vencida</MenuItem>
                </Select>
              </FormControl>
              {hayFiltros && (
                <Button size="small" onClick={handleLimpiar}
                  sx={{ color: GREY_TEXT, fontSize: '0.75rem', textTransform: 'none' }}>
                  Limpiar filtros
                </Button>
              )}
              <Typography variant="caption" sx={{ color: GREY_TEXT, ml: 'auto' }}>
                {total.toLocaleString('es-AR')} registros encontrados
              </Typography>
            </Box>

            {sinResultados && !loading && (
              <Alert severity="info" sx={{ m: 2 }}>
                No se encontraron comprobantes con los filtros aplicados. Revisá el texto ingresado o limpiá los filtros.
              </Alert>
            )}

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: CALF_GREEN }} /></Box>}

            {!loading && !sinResultados && (
              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 'calc(90vh - 300px)' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {COLS.map(col => (
                        <TableCell key={col.id} align={col.align || 'left'} sx={{
                          width: col.width, minWidth: col.width, fontWeight: 700, fontSize: '0.75rem',
                          bgcolor: '#f5f5f5', color: CALF_BLACK,
                          borderBottom: `3px solid ${CALF_GREEN}`, py: 1, whiteSpace: 'nowrap' }}>
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={`${row.persona}-${row.comprobante}-${idx}`} hover sx={{
                        bgcolor: idx % 2 === 0 ? '#fff' : '#f7faf3',
                        '&:hover': { bgcolor: '#e8f4d9' } }}>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{row.persona}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', minWidth: 180 }}>{row.nombre}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', minWidth: 180 }}>{row.domicilio}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{row.comprobante}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{row.fecha_comprobante}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem', fontWeight: 600,
                          color: row.importe < 0 ? '#d32f2f' : CALF_GREEN_DARK }}>{fmtFull(row.importe)}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{row.fecha_vto}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{row.periodo_vto}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', minWidth: 130 }}>{row.categoria}</TableCell>
                        <TableCell>
                          <Chip label={row.estado_deuda || '—'} size="small" sx={{
                            fontSize: '0.68rem', height: 18,
                            bgcolor: row.estado_deuda === 'Deuda Vencida' ? '#fce4ec' : '#e3f2fd',
                            color:   row.estado_deuda === 'Deuda Vencida' ? '#b71c1c' : '#0d47a1' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={row.estado_servicio || '—'} size="small" sx={{
                            fontSize: '0.68rem', height: 18,
                            bgcolor: row.estado_servicio === 'Conectado' ? '#e8f5e9' : '#f5f5f5',
                            color:   row.estado_servicio === 'Conectado' ? '#2e7d32' : '#616161' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{row.dias_interes}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.marca || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', minWidth: 160 }}>{row.submarca || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', minWidth: 120 }}>{row.localidad}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #f0f0f0', px: 2, py: 1 }}>
        {vista === 'detalle' && (<>
        <TablePagination component="div" count={total} page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPP}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[25, 50, 100, 200]} labelRowsPerPage="Filas:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count.toLocaleString('es-AR')}`}
          sx={{ flex: 1, '.MuiTablePagination-toolbar': { minHeight: 40 } }} />
        <Tooltip title="Descargar todos los registros filtrados en CSV" placement="top">
          <span>
            <Button variant="outlined" size="small"
              disabled={total === 0 || loading}
              onClick={() => handleExport('csv')}
              sx={{ ml: 1, borderColor: CALF_GREEN, color: CALF_GREEN_DARK,
                '&:hover': { borderColor: CALF_GREEN_DARK, bgcolor: '#f0f8e8' } }}>
              <DownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
              CSV
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Descargar en Excel (máx. 1.048.576 filas)" placement="top">
          <span>
            <Button variant="outlined" size="small"
              disabled={total === 0 || loading}
              onClick={() => handleExport('xlsx')}
              sx={{ ml: 0.5, borderColor: CALF_GREEN, color: CALF_GREEN_DARK,
                '&:hover': { borderColor: CALF_GREEN_DARK, bgcolor: '#f0f8e8' } }}>
              <DownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
              XLSX
            </Button>
          </span>
        </Tooltip>
        </>)}
        <Button onClick={onClose} variant="outlined" size="small" sx={{ ml: 2 }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Vista Principal ──────────────────────────────────────────────────────────
export default function DeudaPorTipo() {
  // Mantis #302: la vista abre por defecto con criterio de Vencimiento (no Período).
  const [filtroFecha, setFiltroFecha] = useState('compvto');
  const [totales,     setTotales]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [modalOpen,   setModalOpen]   = useState(false);
  const [modalTipo,   setModalTipo]   = useState(null);

  useEffect(() => {
    const fetchTotales = async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/deudapor-tipo/totales?filtroFecha=${filtroFecha}`,
          { headers: { 'Authorization': localStorage.getItem('token') } }
        );
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const json = await res.json();
        setTotales(json.datos);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    };
    fetchTotales();
  }, [filtroFecha]);

  const handleCardClick = (data) => { setModalTipo(data.tipo); setModalOpen(true); };
  const totalGeneral = totales.reduce((s, t) => s + (t.gerencial || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: { xs: '100px', md: '120px' }, px: 0 }}>
      <Card sx={{ overflow: 'visible' }}>

        {/* ── Toggle filtro de fecha ── */}
        <FiltroFechaToggle value={filtroFecha} onChange={(v) => { setFiltroFecha(v); }} />

        <CardBody>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: CALF_BLACK }}>
              Deuda por Tipo de Usuario
            </Typography>
            <Typography variant="body2" sx={{ color: GREY_TEXT, mt: 0.5 }}>
              Sin considerar el mes actual de vencimiento · Sin Municipalidad.
              Hacé click en cualquier tarjeta para ver los comprobantes individuales.
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={8}>
              <Grid container spacing={2}>
                {loading
                  ? [1,2,3,4,5].map(i => <Grid item xs={12} sm={6} key={i}><KpiCard loading /></Grid>)
                  : totales.map(t => (
                      <Grid item xs={12} sm={6} key={t.tipo}>
                        <KpiCard data={t} onClick={handleCardClick} loading={false} />
                      </Grid>
                    ))
                }
              </Grid>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Box sx={{ border: `1px solid ${CALF_LIGHT}`, borderRadius: 2, p: 2, bgcolor: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,.07)', height: '100%',
                display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: CALF_BLACK, mb: 1 }}>
                  Distribución por tipo
                </Typography>
                {loading
                  ? <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress sx={{ color: CALF_GREEN }} /></Box>
                  : totales.length > 0 && <DonutDeuda totales={totales} />
                }
              </Box>
            </Grid>
          </Grid>

          {!loading && totales.length > 0 && (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', p: 2,
              bgcolor: '#f8fdf4', border: `1px solid ${CALF_LIGHT}`, borderRadius: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: GREY_TEXT, textTransform: 'uppercase', letterSpacing: .5 }}>
                  Total sin Municipalidad
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: CALF_GREEN_DARK }}>
                  {fmtFull(totalGeneral)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {filtroFecha === 'periodo'
                  ? <><CalendarMonthIcon sx={{ fontSize: 16, color: GREY_TEXT }} />
                      <Typography variant="caption" sx={{ color: GREY_TEXT }}>Filtrado por <strong>Período de Vto.</strong></Typography></>
                  : <><EventAvailableIcon sx={{ fontSize: 16, color: GREY_TEXT }} />
                      <Typography variant="caption" sx={{ color: GREY_TEXT }}>Filtrado por <strong>Fecha de Vencimiento</strong></Typography></>
                }
              </Box>
            </Box>
          )}
        </CardBody>
      </Card>

      <DrillDownModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        tipo={modalTipo}
        filtroFecha={filtroFecha}
      />
    </Box>
  );
}